import React from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import {
  formatCompactCurrency,
  formatCurrency,
  formatNumber,
  formatTokenAmount,
  formatUsd,
  isUsdStable,
} from "../../../utils/formatters";
import { Box, Stack, Typography } from "@mui/material";
import CustomTooltip from "../../cards/subcomponents/CustomTooltip";
import { touchHitArea } from "../../../theme";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { SurfaceCard } from "../../ui/SurfaceCard";
import { tokens } from "../../../theme";

interface PieChartData {
  name: string;
  value: number;
  amount: number;
  usdValue: number;
  color: string;
}

interface DistributionPieChartProps {
  data: PieChartData[];
  title: string;
  infoTooltipText?: string;
  side?: "right" | "left";
  decimals: number;
  /** Replaces the chart (e.g. explanation when the pool is empty). */
  emptyMessage?: React.ReactNode;
  /** Right side of the title row (e.g. a Wallet / Pool / Total switch). */
  action?: React.ReactNode;
}

const DONUT = 104;

/**
 * Two-token split: a small donut (2px surface gap between slices, total in the
 * hole) plus a legend that carries share, amount and USD value.
 */
const DistributionPieChart: React.FC<DistributionPieChartProps> = ({
  data,
  title,
  action,
  infoTooltipText,
  decimals,
  emptyMessage,
}) => {
  const total = data.reduce((acc, d) => acc + (d.value > 0 ? d.value : 0), 0);
  const hasData = total > 0 && !emptyMessage;

  return (
    <SurfaceCard sx={{ height: "100%", containerType: "inline-size" }}>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 2, minHeight: 32 }}>
        <Typography component="h3" sx={{ fontSize: "1rem", fontWeight: 650, letterSpacing: "-0.01em" }}>
          {title}
        </Typography>
        {infoTooltipText && (
          <CustomTooltip placement="top" content={infoTooltipText}>
            {/* 15px glyph, 44px touch target. */}
            <Box component="span" aria-label="About this chart" sx={{ position: "relative", display: "grid", ...touchHitArea }}>
              <InfoOutlinedIcon sx={{ fontSize: 15, color: "text.disabled", cursor: "help", display: "block" }} />
            </Box>
          </CustomTooltip>
        )}
        {action && <Box sx={{ ml: "auto !important" }}>{action}</Box>}
      </Stack>

      {!hasData ? (
        <Box
          sx={{
            minHeight: DONUT,
            display: "grid",
            placeItems: "center",
            textAlign: "center",
            px: 2,
            py: 1,
            borderRadius: "14px",
            border: `1px dashed ${tokens.borderStrong}`,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {emptyMessage ?? "No assets"}
          </Typography>
        </Box>
      ) : (
        <Stack
          direction="row"
          alignItems="center"
          sx={{
            gap: { xs: 2, md: 2.5 },
            // Narrow cards (three-up on tablets): donut on top, legend full width.
            "@container (max-width: 270px)": { flexDirection: "column", alignItems: "stretch", gap: 2 },
          }}
        >
          <Box sx={{ width: DONUT, height: DONUT, position: "relative", flexShrink: 0, alignSelf: "center" }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  cx="50%"
                  cy="50%"
                  innerRadius={DONUT / 2 - 14}
                  outerRadius={DONUT / 2}
                  startAngle={90}
                  endAngle={-270}
                  stroke={tokens.card}
                  strokeWidth={2}
                  isAnimationActive={false}
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "grid",
                placeItems: "center",
                pointerEvents: "none",
              }}
              title={formatCurrency(total)}
            >
              <Typography sx={{ fontSize: "0.8125rem", fontWeight: 700, lineHeight: 1 }}>
                {formatCompactCurrency(total)}
              </Typography>
            </Box>
          </Box>

          <Stack spacing={1.25} sx={{ minWidth: 0, flex: 1 }}>
            {data.map((d) => {
              const share = total > 0 ? (Math.max(d.value, 0) / total) * 100 : 0;
              return (
                <Box key={d.name} sx={{ minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box sx={{ width: 10, height: 10, borderRadius: "3px", bgcolor: d.color, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: "0.8125rem", fontWeight: 650 }} noWrap>
                      {d.name}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                      {share > 0 && share < 0.01 ? "<0.01" : share.toFixed(share >= 99.95 ? 0 : 1)}%
                    </Typography>
                  </Stack>
                  <Typography
                    sx={{ fontSize: "0.75rem", color: "text.secondary", pl: 2.25, fontVariantNumeric: "tabular-nums" }}
                    noWrap
                    title={`${formatNumber(d.amount, decimals)} ${d.name} · ${formatCurrency(d.usdValue)}`}
                  >
                    {/* Amount with its unit; a stablecoin's USD value would just repeat it. */}
                    {formatTokenAmount(d.amount)} {d.name}
                    {!isUsdStable(d.name) && ` · ${formatUsd(d.usdValue)}`}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </Stack>
      )}
    </SurfaceCard>
  );
};

export default DistributionPieChart;
